// console forwarder for admin panel (staging/prod)
const DEFAULT_SAMPLE = 0.2;

function isLocalhost() {
  const hn = window.location && window.location.hostname;
  return (
    hn === 'localhost' ||
    hn === '[::1]' ||
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/.test(hn || '')
  );
}

/**
 * Redact PHI/PII from log data before sending to server.
 * Mirrors server-side redaction patterns in client_logs_controller.py
 * 
 * @param {*} input - Data to redact (string, array, object, or primitive)
 * @returns {*} Redacted data
 */
function redact(input) {
  if (typeof input === 'string') {
    return input
      // ===== Authentication & Secrets =====
      // JWT tokens
      .replace(/eyJ[A-Za-z0-9_-]*\.eyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*/g, '[REDACTED_TOKEN]')
      // API keys and secrets (32+ chars)
      .replace(/\b[a-zA-Z0-9_-]{32,}\b/g, '[REDACTED_KEY]')
      // Bearer tokens
      .replace(/Bearer\s+[A-Za-z0-9_\-\.]+/gi, 'Bearer [REDACTED_TOKEN]')
      
      // ===== Personal Identifiers =====
      // Email addresses
      .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[REDACTED_EMAIL]')
      // Phone numbers (various US formats)
      .replace(/\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g, '[REDACTED_PHONE]')
      // Names in JSON field patterns
      .replace(/(["\']?(?:first_?name|last_?name)["\']?\s*[:\=]\s*["\'])([^"\']+)(["\'])/gi, '$1[REDACTED_NAME]$3')
      
      // ===== Medical & Health Information =====
      // Dates (ISO and US formats) - may be PHI if DOB or due date
      .replace(/\b\d{4}-\d{2}-\d{2}\b/g, '[REDACTED_DATE]')
      .replace(/\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/g, '[REDACTED_DATE]')
      // Medical IDs
      .replace(/(["\']?(?:healthie_?id|wic_?id|medical_?record|patient_?id|mrn)["\']?\s*[:\=]\s*["\'])([^"\']+)(["\'])/gi, '$1[REDACTED_ID]$3')
      // SSN
      .replace(/\b\d{3}-\d{2}-\d{4}\b/g, '[REDACTED_SSN]')
      
      // ===== Financial Information =====
      // Credit card numbers
      .replace(/\b(?:\d{4}[-\s]?){3}\d{4}\b/g, '[REDACTED_CARD]')
      // Bank account numbers (8-17 digits)
      .replace(/\b\d{8,17}\b/g, '[REDACTED_ACCOUNT]')
      
      // ===== Geographic Information =====
      // ZIP codes
      .replace(/\b\d{5}(?:-\d{4})?\b/g, '[REDACTED_ZIP]')
      // Street addresses
      .replace(/\b\d+\s+[\w\s]+(?:Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Lane|Ln|Drive|Dr|Court|Ct|Circle|Cir|Way)\b/gi, '[REDACTED_ADDRESS]')
      
      // ===== Additional Patterns =====
      // Recurly UUIDs
      .replace(/(["\']?recurly_?uuid["\']?\s*[:\=]\s*["\'])([^"\']+)(["\'])/gi, '$1[REDACTED_UUID]$3')
      // Organization keys
      .replace(/(["\']?(?:api_?key|secret_?key|sdk_?api_?key)["\']?\s*[:\=]\s*["\'])([^"\']+)(["\'])/gi, '$1[REDACTED_KEY]$3');
  }
  if (Array.isArray(input)) return input.slice(0, 10).map(redact);
  if (input && typeof input === 'object') {
    const obj = {};
    const entries = Object.entries(input).slice(0, 20);
    for (const [k, v] of entries) obj[String(k).slice(0, 50)] = redact(v);
    return obj;
  }
  return input;
}

let currentIngestToken = null;
let refreshTimer = null;

async function fetchIngestToken(v2ApiUrl) {
  try {
    const res = await fetch(`${v2ApiUrl.replace(/\/$/, '')}/client-logs/session`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('AUTH_ID_TOKEN') || ''}` },
      credentials: 'omit',
    });
    if (!res.ok) return null;
    const body = await res.json().catch(() => ({}));
    return (body && body.ingest_token) || null;
  } catch {
    return null;
  }
}

function scheduleTokenRefresh(v2ApiUrl) {
  if (refreshTimer) window.clearTimeout(refreshTimer);
  // refresh around 8 minutes
  refreshTimer = window.setTimeout(async () => {
    currentIngestToken = await fetchIngestToken(v2ApiUrl);
    scheduleTokenRefresh(v2ApiUrl);
  }, 8 * 60 * 1000);
}

async function ensureToken(v2ApiUrl) {
  if (currentIngestToken) return;
  currentIngestToken = await fetchIngestToken(v2ApiUrl);
  if (currentIngestToken) scheduleTokenRefresh(v2ApiUrl);
}

function sendViaFetch(v2ApiUrl, payload) {
  return fetch(`${v2ApiUrl.replace(/\/$/, '')}/client-logs`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('AUTH_ID_TOKEN') || ''}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    keepalive: true,
    credentials: 'omit',
  }).catch(() => void 0);
}

function sendViaBeacon(v2ApiUrl, payload) {
  const body = JSON.stringify(payload);
  const blob = new Blob([body], { type: 'application/json' });
  if (!navigator.sendBeacon(`${v2ApiUrl.replace(/\/$/, '')}/client-logs`, blob)) {
    // fallback to fetch if beacon fails
    sendViaFetch(v2ApiUrl, payload);
  }
}

/**
 * Install console forwarder and register with centralized logger
 * @param {object} opts - Options including v2ApiUrl, appVersion, sampleRate
 * @returns {object|null} - Wrapped console backend or null if not enabled
 */
export function installConsoleForwarder(opts) {
  const v2ApiUrl = opts && opts.v2ApiUrl;
  const appVersion = (opts && opts.appVersion) || (window && window.__APP_VERSION__) || 'unknown';
  const sampleRate = (opts && typeof opts.sampleRate === 'number' ? opts.sampleRate : DEFAULT_SAMPLE);

  // Run in development/staging/production environments except true local (localhost)
  if (!v2ApiUrl || isLocalhost()) return null; // local: keep native console only
  if (Math.random() > sampleRate) return null;

  const original = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
  };

  const buildPayload = (level, args) => ({
    level,
    message: redact(args),
    time: new Date().toISOString(),
    pathname: location.pathname,
    user_agent: navigator.userAgent,
    app_version: appVersion,
    ingest_token: currentIngestToken,
  });

  const wrappedBackend = {
    log: (...args) => {
      try {
        const payload = buildPayload('log', args);
        sendViaFetch(v2ApiUrl, payload);
      } catch {}
      original.log(...args);
    },
    info: (...args) => {
      try {
        const payload = buildPayload('info', args);
        sendViaFetch(v2ApiUrl, payload);
      } catch {}
      original.info(...args);
    },
    warn: (...args) => {
      try {
        const payload = buildPayload('warn', args);
        sendViaFetch(v2ApiUrl, payload);
      } catch {}
      original.warn(...args);
    },
    error: (...args) => {
      try {
        const payload = buildPayload('error', args);
        sendViaFetch(v2ApiUrl, payload);
      } catch {}
      original.error(...args);
    },
  };

  // Override global console with wrapped version
  console.log = wrappedBackend.log;
  console.info = wrappedBackend.info;
  console.warn = wrappedBackend.warn;
  console.error = wrappedBackend.error;

  // Unload / background flush with beacon
  const beaconHandler = () => {
    try {
      const payload = buildPayload('info', ['flush']);
      if (currentIngestToken) sendViaBeacon(v2ApiUrl, payload);
    } catch {}
  };

  // Acquire/refresh ingest token proactively
  ensureToken(v2ApiUrl);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') beaconHandler();
  });
  window.addEventListener('pagehide', beaconHandler);

  return wrappedBackend;
}

